import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { ChakraProvider, Spinner } from '@chakra-ui/react';
import amplifyConfig from './config/amplifyConfig';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { EnvironmentProvider } from './context/EnvironmentContext';
import { ParentAgentProvider } from './context/ParentAgentContext';
import { ConversationProvider } from './context/ConversationContext';

// Pages
import Login from './pages/Login';
import Homepage from './pages/Homepage';
import AddNewAgent from './pages/AddNewAgent';
import AgentList from './pages/AgentList';
import AgentsList from './pages/AgentsList';
import AgentDetails from './pages/AgentDetails';
import AgentChat from './pages/AgentChat';
import KnowledgeBase from './pages/KnowledgeBase';
import MiniAgentDevelopment from './pages/MiniAgentDevelopment';
import MicroAgentDevelopment from './pages/MicroAgentDevelopment';
import MicroServiceDevelopment from './pages/MicroServiceDevelopment';
import AgentTestingPage from './pages/AgentTestingPage';
import MiniAgentTestingPage from './pages/MiniAgentTestingPage';
import ServiceTestingPage from './pages/ServiceTestingPage';
import LogsReview from './pages/LogsReview';
import UserProfile from './pages/UserProfile';
import UserAdmin from './pages/UserAdmin';
import ResetPassword from './pages/ResetPassword';
import Signup from './pages/Signup';
import AgentSpecific from './pages/AgentSpecific';
import DeployAgent from './pages/DeployAgent';
import Configuration from './pages/Configuration';
import AiModelsPage from './pages/AiModelsPage';
import AgentStructurePage from './pages/AgentStructurePage'; // New Page

// Components
import Navigation from './components/Navigation';
import ErrorBoundary from './components/ErrorBoundary';
import configService from './services/configService';

// Configure Amplify
try {
  Amplify.configure(amplifyConfig);
} catch (error) {
  console.error('Error configuring Amplify:', error);
}

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Admin route component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  return children;
};

function App() {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState(null);

  useEffect(() => {
    // Load config on app start
    configService.fetchConfig()
      .then(() => {
        console.log('✅ App configuration loaded');
        setConfigLoaded(true);
      })
      .catch(error => {
        console.error('❌ Failed to load app configuration:', error);
        setConfigError(error.message);
        // Still set loaded to true to use fallback config
        setConfigLoaded(true);
      });
  }, []);

  if (!configLoaded) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-3">Loading application...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {configError && (
        <div className="alert alert-warning m-3">
          Using fallback configuration. Some features may be limited.
        </div>
      )}
    <ErrorBoundary>
      <ChakraProvider>
        <AuthProvider>
          <EnvironmentProvider>
            <ParentAgentProvider>
              <ConversationProvider>
                <div className="App">
                  <Navigation />
                  <div className="main-container">
                    <Routes>
                      {/* Public routes */}
                      <Route path="/login" element={<Login />} />
                      <Route path="/signup" element={<Signup />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      
                      {/* Protected routes */}
                      <Route path="/" element={
                        <ProtectedRoute>
                          <Homepage />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/chat" element={
                        <ProtectedRoute>
                          <AgentChat />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/knowledge-base" element={
                        <ProtectedRoute>
                          <KnowledgeBase />
                        </ProtectedRoute>
                      } />
                      
                      {/* AI Models */}
                      <Route path="/ai-models" element={
                        <ProtectedRoute>
                          <AiModelsPage />
                        </ProtectedRoute>
                      } />

                      {/* NEW ROUTE: Agent Structure */}
                      <Route path="/agent-structure" element={
                        <ProtectedRoute>
                          <AgentStructurePage />
                        </ProtectedRoute>
                      } />
                      
                      {/* Agent list and browse - UPDATED */}
                      <Route path="/agents/list" element={
                        <ProtectedRoute>
                          <AgentList />
                        </ProtectedRoute>
                      } />
                      
                      {/* Agent development routes */}
                      <Route path="/agents/add" element={
                        <ProtectedRoute>
                          <AddNewAgent />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/agents/develop" element={
                        <ProtectedRoute>
                          <AgentsList />
                        </ProtectedRoute>
                      } />
                      <Route path="/agents/deploy" element={
                        <ProtectedRoute>
                          <DeployAgent />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/agents/deploy/:agentId" element={
                        <ProtectedRoute>
                          <DeployAgent />
                        </ProtectedRoute>
                      } />
                      
                      {/* Parent agent and specific agent routes */}
                      <Route path="/parent/:parentId" element={
                        <ProtectedRoute>
                          <AgentDetails />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/parent/:parentId/agent/:id" element={
                        <ProtectedRoute>
                          <AgentSpecific />
                        </ProtectedRoute>
                      } />
                      
                      {/* Configuration routes */}
                      <Route path="/agents/:agentId/configuration" element={
                        <ProtectedRoute>
                          <Configuration />
                        </ProtectedRoute>
                      } />
                      
                      {/* Mini-agent routes */}
                      <Route path="/agents/:agentId/mini-agents/:miniAgentId" element={
                        <ProtectedRoute>
                          <MiniAgentDevelopment />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/agents/:agentId/mini-agents/:miniAgentId/testing" element={
                        <ProtectedRoute>
                          <MiniAgentTestingPage />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/agents/:agentId/mini-agents/:miniAgentId/logs" element={
                        <ProtectedRoute>
                          <LogsReview />
                        </ProtectedRoute>
                      } />
                      
                      {/* Micro-agent routes */}
                      <Route path="/agents/:agentId/micro-agents/:microAgentId" element={
                        <ProtectedRoute>
                          <MicroAgentDevelopment />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/agents/:agentId/micro-agents/:microAgentId/testing" element={
                        <ProtectedRoute>
                          <AgentTestingPage />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/agents/:agentId/micro-agents/:microAgentId/logs" element={
                        <ProtectedRoute>
                          <LogsReview />
                        </ProtectedRoute>
                      } />
                      
                      {/* Micro-service routes */}
                      <Route path="/agents/:agentId/micro-services/:microServiceId" element={
                        <ProtectedRoute>
                          <MicroServiceDevelopment />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/agents/:agentId/micro-services/:microServiceId/testing" element={
                        <ProtectedRoute>
                          <ServiceTestingPage />
                        </ProtectedRoute>
                      } />
                      
                      {/* User routes */}
                      <Route path="/user-profile" element={
                        <ProtectedRoute>
                          <UserProfile />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/user-admin" element={
                        <AdminRoute>
                          <UserAdmin />
                        </AdminRoute>
                      } />
                      
                      {/* Old-style routes for backward compatibility */}
                      <Route path="/agent/:agentId/mini-agent/:miniAgentId" element={
                        <ProtectedRoute>
                          <Navigate to={location => `/agents/${location.pathname.split('/')[2]}/mini-agents/${location.pathname.split('/')[4]}`} />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/agent/:agentId/micro-agent/:microAgentId" element={
                        <ProtectedRoute>
                          <Navigate to={location => `/agents/${location.pathname.split('/')[2]}/micro-agents/${location.pathname.split('/')[4]}`} />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/agent/:agentId" element={
                        <ProtectedRoute>
                          <Navigate to={location => `/agents/${location.pathname.split('/')[2]}`} />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/add-new-agent" element={
                        <ProtectedRoute>
                          <Navigate to="/agents/add" />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/develop-existing-agent" element={
                        <ProtectedRoute>
                          <Navigate to="/agents/develop" />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/deploy-agent" element={
                        <ProtectedRoute>
                          <Navigate to="/agents/deploy" />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/profile" element={
                        <ProtectedRoute>
                          <Navigate to="/user-profile" />
                        </ProtectedRoute>
                      } />
                      
                      {/* Catch-all redirect to home */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </div>
                </div>
              </ConversationProvider>
            </ParentAgentProvider>
          </EnvironmentProvider>
        </AuthProvider>
      </ChakraProvider>
    </ErrorBoundary>
    </div>
  );
}

export default App;
